import { formatCurrency } from './CurrencyUtils';
import { formatPackageDuration } from './packageDuration';

// Load contact phone from localStorage
const getContactPhone = (): string => {
  const savedContent = localStorage.getItem('website_content');
  if (savedContent) {
    try {
      const parsed = JSON.parse(savedContent);
      if (parsed.contactPhone) {
        return parsed.contactPhone;
      }
    } catch (error) {
      console.error('Error loading contact phone:', error);
    }
  }
  // Default fallback
  return '+27 (0) 82 123 4567';
};

// Clean phone number for WhatsApp (remove spaces, parentheses, but keep the + prefix)
const cleanPhoneNumber = (phone: string): string => {
  // Remove spaces, parentheses, and other formatting, but keep the + sign
  return phone.replace(/\s+/g, '').replace(/[()]/g, '');
};

let lastWhatsAppOpenAt = 0;

/** One wa.me link. A second handoff makes WhatsApp append the same draft again. */
export function whatsAppChatUrl(phone: string, message: string): string {
  const digits = String(phone || '').replace(/\D/g, '');
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

export function openWhatsAppChat(phone: string, message: string): void {
  const now = Date.now();
  if (now - lastWhatsAppOpenAt < 2000) return;
  const url = whatsAppChatUrl(phone, message);
  if (!url.startsWith('https://wa.me/') || url.startsWith('https://wa.me/?')) return;
  lastWhatsAppOpenAt = now;
  const popup = window.open(url, '_blank', 'noopener,noreferrer');
  if (!popup) window.location.assign(url);
}

// Generate WhatsApp contact URL for a package
export const generateWhatsAppContactUrl = (
  packageName: string,
  displayPrice: number | null,
  displayCurrency: string,
  originalPrice?: number | null,
  durationMinutes?: number | null
): string => {
  const phone = getContactPhone();
  const cleanPhone = cleanPhoneNumber(phone);
  
  // Build message with package name and price
  let message = `Hi! I'm interested in the *${packageName}* package.`;
  
  if (displayPrice) {
    const priceText = formatCurrency(displayPrice, displayCurrency);
    message += `\n\nPrice: ${priceText}`;
    
    // Add original price if there's a discount
    if (originalPrice && originalPrice > displayPrice) {
      const originalPriceText = formatCurrency(originalPrice, displayCurrency);
      message += ` (was ${originalPriceText})`;
    }
  } else {
    message += '\n\nPlease let me know the pricing details.';
  }

  const coverage = formatPackageDuration(durationMinutes);
  if (coverage) message += `\nDuration: ${coverage}`;
  
  message += '\n\nCould we chat about this package?';
  
  // Generate WhatsApp URL
  // WhatsApp URLs need the country code without the + sign
  const phoneForUrl = cleanPhone.replace(/^\+/, '');
  const encodedMessage = encodeURIComponent(message);
  const whatsappUrl = `https://wa.me/${phoneForUrl}?text=${encodedMessage}`;
  
  return whatsappUrl;
};

// Handle WhatsApp contact click (with fallback for different devices)
export const handleWhatsAppContact = (
  packageName: string,
  displayPrice: number | null,
  displayCurrency: string,
  originalPrice?: number | null,
  durationMinutes?: number | null
): void => {
  const phone = getContactPhone();
  const cleanPhone = cleanPhoneNumber(phone);
  
  // Build message with package name and price
  let message = `Hi! I'm interested in the *${packageName}* package.`;
  
  if (displayPrice) {
    const priceText = formatCurrency(displayPrice, displayCurrency);
    message += `\n\nPrice: ${priceText}`;
    
    // Add original price if there's a discount
    if (originalPrice && originalPrice > displayPrice) {
      const originalPriceText = formatCurrency(originalPrice, displayCurrency);
      message += ` (was ${originalPriceText})`;
    }
  } else {
    message += '\n\nPlease let me know the pricing details.';
  }

  const coverage = formatPackageDuration(durationMinutes);
  if (coverage) message += `\nDuration: ${coverage}`;
  
  message += '\n\nCould we chat about this package?';
  openWhatsAppChat(cleanPhone, message);
};
